import { useEffect, useMemo } from "react";
import { Link, useParams, useLocation, Navigate } from "@/lib/router-compat";
import { saveScroll, setLastListPath, setLastFrameSlug } from "@/lib/scrollMemory";
import { Eye, Store, MapPin, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Disclosure } from "@/components/ui/disclosure";
import { useCountry } from "@/hooks/useCountry";
import { useFrameCatalog } from "@/hooks/useFrameCatalog";
import { useCountryTopology } from "@/hooks/useCountryTopology";
import {
  parseFacetKey,
  findFacetValue,
  frameMatchesFacet,
  getCollectionFacets,
  getIndexableValues,
  getFacetSlug,
  FACET_PATH_SLUGS,
  type FacetKey,
} from "@/lib/facets";
import { getFacetCopy } from "@/lib/facetSeoCopy";
import { getFacetEditorial } from "@/content/facets";
import {
  FacetEditorialSection,
  FacetHowToChoose,
  FacetPriceSection,
  FacetCrossLinks,
} from "@/components/facets/FacetSections";
import { GuidesTeaserSection } from "@/components/guides/GuidesTeaserSection";
import { VHITeaserSection } from "@/components/visionHealth/VHITeaserSection";
import { getQuickPrices } from "@/config/quickPrices";
import { PILLAR_GUIDE, FACE_SHAPE_HUB, guidePath } from "@/content/guides/registry";
import { SchemaGraph } from "@/components/seo/SchemaGraph";
import { breadcrumbNode, faqNode, collectionPageNode, itemListNode, productListItemNode, aggregateOfferFromFrames, organizationNode, websiteNode, chainOpticianNode } from "@/lib/schema/generators";
import { useCountryContent } from "@/hooks/useCountryContent";
import { frameImageUrl } from "@/lib/frameImage";
import { buildWhatsAppUrl } from "@/lib/whatsapp";

const BASE = "https://lapaire.africa";

export default function FacetGlassesPage() {
  const { country: countrySlug, facet: facetParam, value: valueParam } = useParams();
  const { country, lang, formatPrice, t } = useCountry();
  const location = useLocation();

  useEffect(() => {
    setLastListPath(location.pathname + location.search);
  }, [location.pathname, location.search]);


  const facetKey: FacetKey | null = facetParam ? parseFacetKey(facetParam) : null;
  const { data: frames = [], isLoading } = useFrameCatalog(country.id);

  const dynamicCollectionValues = useMemo(
    () => (facetKey === "collection" ? getCollectionFacets(frames) : undefined),
    [facetKey, frames]
  );

  const facetValue = useMemo(() => {
    if (!facetKey || !valueParam) return null;
    return findFacetValue(facetKey, valueParam, dynamicCollectionValues);
  }, [facetKey, valueParam, dynamicCollectionValues]);

  // Cities pour le maillage SEO — topologie pays partagée (cache 30 min).
  const { data: topology } = useCountryTopology(country.slug);
  const cities = (topology?.cities ?? []).slice(0, 6);
  const cc = useCountryContent();


  const matchedFrames = useMemo(() => {
    if (!facetKey || !facetValue) return [];
    return frames.filter((f) => frameMatchesFacet(f, facetKey, facetValue));
  }, [frames, facetKey, facetValue]);

  // Facette vide : aucun prix ne doit être affiché (sinon « Dès 0 FCFA »).
  const hasResults = matchedFrames.length > 0;
  const isEmptyFacet = !isLoading && !hasResults;

  const startingPrice = useMemo(() => {
    if (matchedFrames.length === 0) return "";
    return formatPrice(Math.min(...matchedFrames.map((f) => f.startingPrice)));
  }, [matchedFrames, formatPrice]);

  // Other values for the same facet (internal linking)
  const otherValues = useMemo(() => {
    if (!facetKey) return [];
    return getIndexableValues(facetKey, frames).filter((v) => v.id !== facetValue?.id);
  }, [facetKey, frames, facetValue]);

  // Analytics: product_list_view (facet listing)
  useEffect(() => {
    if (!facetKey || !facetValue) return;
    import("@/lib/analytics/track").then(({ trackIdle }) => {
      trackIdle("product_list_view", {
        product_list_name: `facet_${facetKey}`,
        product_category: facetKey === "shape" ? facetValue.id : undefined,
        facet_key: facetKey,
        facet_value: facetValue.id,
        page_path_clean: location.pathname,
      });
    });
  }, [facetKey, facetValue?.id, location.pathname]);

  // ----- SEO -----
  const facetPathSlug = facetKey ? FACET_PATH_SLUGS[lang][facetKey] : "";
  const valueSlug = facetValue ? getFacetSlug(facetValue, lang) : "";
  const glassesPathSlug = lang === "fr" ? "lunettes" : "glasses";
  const url = `${BASE}/${country.slug}/${glassesPathSlug}/${facetPathSlug}/${valueSlug}`;
  const label = facetValue ? (lang === "fr" ? facetValue.label_fr : facetValue.label_en) : "";

  const isGender = facetKey === "gender";
  const h1 = lang === "fr"
    ? (isGender
        ? `Lunettes de vue ${label} ${country.preposition} ${country.name}`
        : `Lunettes ${label} ${country.preposition} ${country.name}`)
    : (isGender
        ? `${label.charAt(0).toUpperCase()}${label.slice(1)} prescription glasses in ${country.name}`
        : `${label.charAt(0).toUpperCase()}${label.slice(1)} glasses in ${country.name}`);




  const citiesInline = cities.slice(0, 3).map((c: any) => c.name).join(", ") || country.name;
  // Pendant le chargement, on ne génère aucune copie : sinon la variante
  // « zéro résultat » s'afficherait le temps d'un frame.
  const copy = facetKey && !isLoading
    ? getFacetCopy(lang, facetKey, {
        country: country.name,
        prep: country.preposition,
        label,
        count: matchedFrames.length,
        price: startingPrice,
        currency: country.currencySymbol,
        cities: citiesInline,
      })
    : null;

  const metaDesc = copy?.intro ?? "";

  // Contenu éditorial dédié à la valeur de facette (null → fallback générique).
  const editorial = getFacetEditorial(facetKey, facetValue?.id);
  const editorialCopy = editorial ? editorial[lang] : null;

  // FAQ affichée = FAQ éditoriale spécifique + FAQ générique du template.
  // Le JSON-LD FAQPage utilise exactement cette liste (règle Google).
  const faqItems = [...(editorialCopy?.faq ?? []), ...(copy?.faq ?? [])];

  const quickPrices = getQuickPrices(country.slug);
  const framePriceLabel = quickPrices ? formatPrice(quickPrices.frameOnly) : undefined;

  // Le head (title, description, canonical, hreflang) est émis en SSR par
  // `facetHead` — aucune réécriture côté client, sous peine de canonicals
  // divergents entre le HTML servi et le DOM hydraté.


  // ---- Guards ----
  if (!facetKey || !facetParam) return <Navigate to={`/${country.slug}/${glassesPathSlug}`} replace />;
  if (!isLoading && !facetValue) return <Navigate to={`/${country.slug}/${glassesPathSlug}`} replace />;

  const productItems = matchedFrames.slice(0, 20).map((f) =>
    productListItemNode({
      url: `/${country.slug}/product/${f.slug}`,
      name: (lang === "en" && f.name_en) || f.name,
      image: (f.base_image_url ? frameImageUrl(f.base_image_url, "card") : null) ?? null,
      sku: f.sku,
      startingPrice: f.startingPrice,
      inStock: f.inStock,
      currency: country.currency,
      lang,
    })
  );

  const pageId = `${url}#collection`;
  const breadcrumbId = `${url}#breadcrumb`;
  const faqId = `${url}#faq`;
  const productsListId = `${url}#products`;
  const sameAs = [cc.socials.facebook, cc.socials.instagram, cc.socials.linkedin].filter(Boolean);

  return (
    <div>
      <SchemaGraph
        nodes={[
          organizationNode({
            lang,
            countryName: country.name,
            whatsappNumber: country.whatsappNumber,
            sameAs,
          }),
          websiteNode(lang),
          chainOpticianNode({
            countrySlug: country.slug,
            countryName: country.name,
            countryCode: country.code,
            countryPreposition: country.preposition as "au" | "en",
            currency: country.currency,
            whatsappNumber: country.whatsappNumber,
            numberOfBranches: topology?.totalBranches || cc.branchesEstimate,
            lang,
            sameAs,
          }),
          breadcrumbNode(
            [
              { name: country.name, url: `/${country.slug}` },
              { name: lang === "fr" ? "Lunettes" : "Glasses", url: `/${country.slug}/${glassesPathSlug}` },
              { name: h1, url },
            ],
            breadcrumbId,
          ),
          facetValue &&
            collectionPageNode({
              id: pageId,
              name: h1,
              url,
              description: metaDesc,
              lang,
              breadcrumbId,
              about: { name: h1, description: editorialCopy?.suits ?? metaDesc },
              significantLinks: [
                `/${country.slug}/${glassesPathSlug}`,
                guidePath(country.slug, PILLAR_GUIDE, lang),
                guidePath(country.slug, FACE_SHAPE_HUB, lang),
                `/${country.slug}/prices`,
                `/${country.slug}/book`,
              ],
              // L'ItemList est toujours émise, même vide (numberOfItems: 0) :
              // un CollectionPage sans mainEntity est incohérent pour les
              // moteurs, alors qu'une liste vide est valide et explicite.
              itemList: itemListNode({
                id: productsListId,
                name: h1,
                numberOfItems: matchedFrames.length,
                items: productItems,
              }),
            }),
          // Pas d'AggregateOffer sans produit : aucun prix n'existe.
          hasResults &&
            aggregateOfferFromFrames({
              frames: matchedFrames,
              currency: country.currency,
              url,
            }),
          faqItems.length > 0 && faqNode(faqItems, faqId),
        ]}
      />


      {/* HERO */}
      <section className="bg-primary text-primary-foreground">
        <div className="container py-10 md:py-14">
          <nav aria-label="Breadcrumb" className="text-xs opacity-80 mb-3">
            <Link to={`/${country.slug}`} className="hover:underline">{country.name}</Link>
            <span className="mx-2">/</span>
            <Link to={`/${country.slug}/${glassesPathSlug}`} className="hover:underline">
              {lang === "fr" ? "Lunettes" : "Glasses"}
            </Link>
          </nav>
          <h1 className="text-3xl md:text-5xl font-bold mb-3 leading-tight">{h1}</h1>
          <p className="text-base md:text-lg opacity-90 max-w-2xl">{copy?.intro}</p>
        </div>
      </section>

      {/* INTRO COPY */}
      <section className="container py-8 max-w-3xl">
        <p className="text-base text-foreground/90 mb-4">{copy?.paragraph1}</p>
        <p className="text-base text-foreground/90">{copy?.paragraph2}</p>
      </section>

      {/* PRODUCT GRID */}
      <section className="container pb-10">
        <h2 className="text-xl md:text-2xl font-bold mb-4">
          {isEmptyFacet
            ? lang === "fr"
              ? `Aucune monture ${label} en stock ${country.preposition} ${country.name} pour le moment`
              : `No ${label} frames in stock in ${country.name} right now`
            : lang === "fr"
              ? `Nos modèles ${label} disponibles ${country.preposition} ${country.name}`
              : `Our ${label} models in ${country.name}`}
        </h2>

        {isLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="aspect-square rounded-lg" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            ))}
          </div>
        ) : matchedFrames.length === 0 ? (
          <div className="border border-border rounded-lg p-6 md:p-8 bg-muted/30">
            <p className="text-base text-foreground/90 max-w-2xl">
              {lang === "fr"
                ? "Notre catalogue est renouvelé chaque semaine : cette sélection peut revenir en stock d'un jour à l'autre. En attendant, voici ce que vous pouvez faire."
                : "Our catalogue is refreshed every week, so this selection can come back in stock any day. In the meantime, here is what you can do."}
            </p>

            <div className="flex flex-wrap gap-3 mt-5">
              <Button asChild>
                <Link to={`/${country.slug}/${glassesPathSlug}`}>
                  {lang === "fr" ? "Voir tout le catalogue" : "Browse full catalogue"}
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to={`/${country.slug}/book`}>
                  {lang === "fr" ? "Réserver un test de vue gratuit" : "Book a free eye test"}
                </Link>
              </Button>
              {country.whatsappNumber && (
                <Button
                  variant="outline"
                  onClick={() => {
                    const href = buildWhatsAppUrl(
                      country.whatsappNumber,
                      lang === "fr"
                        ? `Bonjour, je cherche des lunettes ${label} ${country.preposition} ${country.name}. Avez-vous des modèles disponibles ?`
                        : `Hello, I'm looking for ${label} glasses in ${country.name}. Do you have any available?`,
                    );
                    if (href) window.open(href, "_blank", "noopener");
                  }}
                >
                  {lang === "fr" ? "Nous écrire sur WhatsApp" : "Message us on WhatsApp"}
                </Button>
              )}
            </div>

            {otherValues.length > 0 && (
              <div className="mt-6">
                <p className="text-sm font-medium mb-2">
                  {lang === "fr" ? "Styles disponibles en ce moment :" : "Styles available right now:"}
                </p>
                <div className="flex flex-wrap gap-2">
                  {otherValues.slice(0, 8).map((v) => (
                    <Link
                      key={v.id}
                      to={`/${country.slug}/${glassesPathSlug}/${facetPathSlug}/${getFacetSlug(v, lang)}`}
                      className="text-sm border border-border rounded-full px-3 py-1.5 hover:border-primary/40 hover:text-primary transition-colors"
                    >
                      {lang === "fr" ? v.label_fr : v.label_en}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {matchedFrames.map((frame, index) => (
              <Link
                key={frame.id}
                id={`frame-${frame.slug}`}
                to={`/${country.slug}/product/${frame.slug}`}
                onClick={() => {
                  saveScroll(location.pathname + location.search);
                  setLastListPath(location.pathname + location.search);
                  setLastFrameSlug(frame.slug);
                }}
                className="group border border-border rounded-lg overflow-hidden hover:border-primary/40 transition-colors"
              >
                <div className="aspect-square bg-secondary/30 overflow-hidden">
                  {frame.base_image_url ? (
                    <img
                      src={frameImageUrl(frame.base_image_url, "card")}
                      alt={
                        lang === "fr"
                          ? `${frame.name} — lunettes ${label} ${country.preposition} ${country.name}`
                          : `${frame.name} — ${label} glasses in ${country.name}`
                      }
                      width={400}
                      height={400}
                      loading={index < 4 ? "eager" : "lazy"}
                      fetchPriority={index === 0 ? "high" : "auto"}
                      decoding={index === 0 ? "sync" : "async"}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">{frame.name}</div>
                  )}
                </div>
                <div className="p-3">
                  <h3 className="text-sm font-medium leading-tight mb-1 line-clamp-2">{frame.name}</h3>
                  <p className="text-xs text-muted-foreground mb-1">
                    {[frame.gender, frame.shape, frame.material].filter(Boolean).join(" · ")}
                  </p>
                  <p className="text-sm font-bold text-primary">
                    {lang === "fr" ? "À partir de " : "From "}{formatPrice(frame.startingPrice)}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* EDITORIAL — est-ce fait pour vous ? */}
      {editorial && editorialCopy && facetKey && (
        <FacetEditorialSection
          editorial={editorial}
          copy={editorialCopy}
          label={label}
          facetKey={facetKey}
        />
      )}

      {/* COMMENT CHOISIR */}
      {editorial && editorialCopy && (
        <FacetHowToChoose
          copy={editorialCopy}
          editorial={editorial}
          ctx={{ countrySlug: country.slug, lang, glassesSegment: glassesPathSlug as "lunettes" | "glasses" }}
        />
      )}

      {/* VERRES & PRIX */}
      <FacetPriceSection
        ctx={{ countrySlug: country.slug, lang, glassesSegment: glassesPathSlug as "lunettes" | "glasses" }}
        priceLabel={framePriceLabel}
      />

      {/* MAILLAGE CROISÉ */}
      {editorial && (
        <FacetCrossLinks
          editorial={editorial}
          ctx={{ countrySlug: country.slug, lang, glassesSegment: glassesPathSlug as "lunettes" | "glasses" }}
        />
      )}

      {/* INTERNAL LINKING — other values */}
      {otherValues.length > 0 && (
        <section className="bg-secondary/40">
          <div className="container py-8">
            <h2 className="text-lg font-bold mb-3">
              {lang === "fr" ? "Voir aussi" : "See also"}
            </h2>
            <div className="flex flex-wrap gap-2">
              {otherValues.map((v) => (
                <Link
                  key={v.id}
                  to={`/${country.slug}/${glassesPathSlug}/${facetPathSlug}/${getFacetSlug(v, lang)}`}
                  className="inline-flex items-center px-3 py-1.5 rounded-full bg-background border border-border text-sm hover:border-primary hover:text-primary transition-colors"
                >
                  {lang === "fr" ? `Lunettes ${v.label_fr}` : `${v.label_en} glasses`}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="container py-10 text-center">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-6">
          <Link
            to={`/${country.slug}/book`}
            onClick={() => {
              import("@/lib/analytics/trackBookCta").then(({ trackBookEyeTestCta }) =>
                trackBookEyeTestCta("facet_glasses_footer", `/${country.slug}/book`)
              );
            }}
          >
            <Button size="lg" className="gap-2">
              <Eye className="h-4 w-4" />
              {t("nav.eyeTest")}
            </Button>
          </Link>
          <Link to={`/${country.slug}/stores`}>
            <Button size="lg" variant="outline" className="gap-2">
              <Store className="h-4 w-4" />
              {t("nav.stores")}
            </Button>
          </Link>
        </div>
        {cities.length > 0 && (
          <div className="flex flex-wrap items-center justify-center gap-2">
            {cities.map((c: any) => (
              <Link key={c.id} to={`/${country.slug}/${c.slug}`} className="inline-flex items-center gap-1 text-sm text-primary hover:underline">
                <MapPin className="h-3 w-3" /> {c.name}
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* GUIDES + SANTÉ VISUELLE */}
      <GuidesTeaserSection />
      <VHITeaserSection />

      {/* FAQ */}
      {faqItems.length > 0 && (
        <section className="bg-secondary/50">
          <div className="container py-10">
            <h2 className="text-2xl font-bold mb-6 text-center">FAQ</h2>
            <div className="max-w-2xl mx-auto">
              {faqItems.map((item, i) => (
                <Disclosure key={i} summary={<span className="text-left">{item.q}</span>}>
                  {item.a}
                </Disclosure>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Back to full catalog */}
      <section className="container py-6 text-center">
        <Link to={`/${country.slug}/${glassesPathSlug}`} className="inline-flex items-center gap-1 text-primary hover:underline text-sm">
          {lang === "fr" ? "Voir toutes les lunettes" : "Browse all glasses"} <ChevronRight className="h-4 w-4" />
        </Link>
      </section>
    </div>
  );
}
